import * as React from "react";
import { useParams } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@src/ui/views/table";
import { Avatar, AvatarImage, AvatarFallback } from "@src/ui/views/avatar";
import { Badge } from "@src/ui/views/badge";
import {
  useGetUsersRolesQuery,
  useAddUserToSpaceMutation,
} from "../../redux/users-api";
import { useAssignDriverToTimedShuttleInstanceMutation } from "@src/features/timed-shuttles/redux/timed-shuttles-api";
import type { AllUserRolesResponse } from "../../users-models";

type ServiceTypeOption = "None" | "timedShuttle";
type RoleNameOption = "None" | "driver";

export function UsersScreen() {
  const { spaceId } = useParams<{ spaceId: string }>();
  const { data, isLoading, isError, refetch } = useGetUsersRolesQuery(
    { spaceId: spaceId! },
    { skip: !spaceId }
  );

  const [addUserModalOpen, setAddUserModalOpen] = React.useState(false);
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [phoneCountryCode, setPhoneCountryCode] = React.useState("+972");
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [email, setEmail] = React.useState("");

  const [serviceType, setServiceType] = React.useState<ServiceTypeOption>("None");
  const [roleName, setRoleName] = React.useState<RoleNameOption>("None");

  const [formError, setFormError] = React.useState<string | null>(null);
  const [createdUserId, setCreatedUserId] = React.useState<string | null>(null);

  const [assignModalOpen, setAssignModalOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<AllUserRolesResponse | null>(null);
  const [rideIdInput, setRideIdInput] = React.useState("");
  const [assignError, setAssignError] = React.useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = React.useState<string | null>(null);

  const [addUserToSpace, { isLoading: isAdding }] = useAddUserToSpaceMutation();
  const [assignUserToRide, { isLoading: isAssigning }] =
    useAssignDriverToTimedShuttleInstanceMutation();

  if (isLoading) return <p className="text-slate-800 dark:text-slate-100">Loading users…</p>;
  if (isError || !data) return <p className="text-red-600 dark:text-red-400">Error loading users</p>;

  const users: AllUserRolesResponse[] = data.usersList;

  async function handleAddUserSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!spaceId) return;

    setFormError(null);
    setCreatedUserId(null);

    if (!firstName.trim() || !lastName.trim() || !phoneNumber.trim() || !email.trim()) {
      setFormError("Please fill in all required fields.");
      return;
    }

    try {
      const result = await addUserToSpace({
        spaceId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneCountryCode: phoneCountryCode.trim(),
        phoneNumber: phoneNumber.trim(),
        email: email.trim(),
        ...(serviceType !== "None" ? { serviceType } : {}),
        ...(roleName !== "None" ? { roleName } : {}),
      }).unwrap();

      if (result && result.userId) setCreatedUserId(result.userId);

      // Keep modal open, but clear inputs for convenience
      setFirstName("");
      setLastName("");
      setPhoneCountryCode("+972");
      setPhoneNumber("");
      setEmail("");
      setServiceType("None");
      setRoleName("None");

      await refetch();
    } catch {
      setFormError("Failed to add user. Please try again.");
    }
  }

  const handleCloseModal = () => {
    setAddUserModalOpen(false);
    setFormError(null);
    setCreatedUserId(null);
    setFirstName("");
    setLastName("");
    setPhoneCountryCode("+972");
    setPhoneNumber("");
    setEmail("");
    setServiceType("None");
    setRoleName("None");
  };

  const openAssignModalForUser = (u: AllUserRolesResponse) => {
    setSelectedUser(u);
    setRideIdInput("");
    setAssignError(null);
    setAssignSuccess(null);
    setAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setAssignModalOpen(false);
    setSelectedUser(null);
    setRideIdInput("");
    setAssignError(null);
    setAssignSuccess(null);
  };

  const handleAssignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!spaceId || !selectedUser) return;

    setAssignError(null);
    setAssignSuccess(null);

    const rideId = rideIdInput.trim();
    if (!rideId) {
      setAssignError("Please enter a rideId.");
      return;
    }

    try {
      await assignUserToRide({
        spaceId,
        rideId,
        userId: selectedUser.userPublicInfo.userId,
      }).unwrap();

      setAssignSuccess(`Assigned user to ride ${rideId} successfully.`);
    } catch {
      setAssignError(
        "Failed to assign user to ride. Please check the rideId and try again."
      );
    }
  };

  return (
    <div className="p-4 space-y-4 bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100 transition-colors">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Users &amp; Roles</h2>
        <button
          type="button"
          onClick={() => {
            setAddUserModalOpen(true);
            setFormError(null);
            setCreatedUserId(null);
            setServiceType("None");
            setRoleName("None");
          }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-100 hover:border-slate-300 active:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
        >
          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-900 text-white text-[10px] dark:bg-slate-100 dark:text-slate-900">
            +
          </span>
          <span>Add user to space</span>
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <Table className="w-full">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/80">
              <TableHead className="text-slate-700 dark:text-slate-200">User Id</TableHead>
              <TableHead className="text-slate-700 dark:text-slate-200">Name</TableHead>
              <TableHead className="text-slate-700 dark:text-slate-200">Roles</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow
                key={u.userPublicInfo.userId}
                onClick={() => openAssignModalForUser(u)}
                className="hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer"
                title="Click to assign this user to a ride"
              >
                <TableCell className="text-xs md:text-sm">
                  {u.userPublicInfo.userId}
                </TableCell>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={u.userPublicInfo.profilePic ?? ""} alt={u.userPublicInfo.firstName} />
                      <AvatarFallback>
                        {(u.userPublicInfo.firstName || u.userPublicInfo.userId)[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm">
                      {u.userPublicInfo.firstName} {u.userPublicInfo.lastName}
                    </span>
                  </div>
                </TableCell>

                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    {u.userRoles.map((r) => (
                      <Badge
                        key={r.serviceName + r.role}
                        variant="outline"
                        className="border-slate-200 text-slate-800 dark:border-slate-600 dark:text-slate-100"
                      >
                        {`${r.serviceName} ${r.role}`}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {addUserModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <form
            onSubmit={handleAddUserSubmit}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-xl p-5 w-full max-w-md space-y-4 border border-slate-200 dark:border-slate-700"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Add user to space
              </h3>
              <button
                type="button"
                onClick={handleCloseModal}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            {formError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300">
                {formError}
              </div>
            )}

            {createdUserId && (
              <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300">
                User created successfully. New userId:{" "}
                <span className="font-mono break-all">{createdUserId}</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
                  First name<span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
                  Last name<span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-[0.8fr,2.2fr] gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
                  Country code<span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={phoneCountryCode}
                  onChange={(e) => setPhoneCountryCode(e.target.value)}
                  placeholder="+972"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
                  Phone number<span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="54 123 4567"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
                Email<span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className="w-full border border-slate-200 dark:border-slate-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
                  Service type (optional)
                </label>
                <select
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value as ServiceTypeOption)}
                >
                  <option value="None">None</option>
                  <option value="timedShuttle">timedShuttle</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
                  Role name (optional)
                </label>
                <select
                  className="w-full border border-slate-200 dark:border-slate-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value as RoleNameOption)}
                >
                  <option value="None">None</option>
                  <option value="driver">driver</option>
                </select>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              The phone number may be verified by SMS depending on backend logic.
            </p>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isAdding}
                className="px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white font-medium disabled:bg-slate-300 disabled:text-slate-600 dark:bg-blue-500 dark:disabled:bg-slate-700"
              >
                {isAdding ? "Adding…" : "Add"}
              </button>
            </div>
          </form>
        </div>
      )}

      {assignModalOpen && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <form
            onSubmit={handleAssignSubmit}
            className="bg-white dark:bg-slate-900 rounded-xl shadow-xl p-5 w-full max-w-md space-y-4 border border-slate-200 dark:border-slate-700"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Assign driver to ride
              </h3>
              <button
                type="button"
                onClick={closeAssignModal}
                className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <div className="text-sm text-slate-700 dark:text-slate-200">
              Selected user:{" "}
              <span className="font-medium">
                {selectedUser.userPublicInfo.firstName} {selectedUser.userPublicInfo.lastName}
              </span>{" "}
              <span className="block text-xs text-slate-500 dark:text-slate-400 font-mono break-all">
                {selectedUser.userPublicInfo.userId}
              </span>
            </div>

            {assignError && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 dark:bg-red-900/30 dark:border-red-600 dark:text-red-300">
                {assignError}
              </div>
            )}

            {assignSuccess && (
              <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 dark:bg-green-900/30 dark:border-green-600 dark:text-green-300">
                {assignSuccess}
              </div>
            )}

            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-200 mb-1">
                Ride ID<span className="text-red-500">*</span>
              </label>
              <input
                className="w-full border border-slate-200 dark:border-slate-600 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={rideIdInput}
                onChange={(e) => setRideIdInput(e.target.value)}
                placeholder="e.g. r3e296894--2025-11-27"
                autoFocus
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={closeAssignModal}
                className="px-3 py-1.5 text-sm rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isAssigning}
                className="px-3 py-1.5 text-sm rounded-md bg-slate-900 text-white font-medium disabled:bg-slate-300 disabled:text-slate-600 dark:bg-slate-100 dark:text-slate-900 dark:disabled:bg-slate-700 dark:disabled:text-slate-300"
              >
                {isAssigning ? "Assigning…" : "Assign as driver"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}